<template>
    <div class="container">
        <div class="row">
            <div class="col-12">
                <div class="breadcrumb">
                    <router-link :to="{name: 'document-templates'}"
                                 class="is-active">
                        <i class="icons icon-arrow-left"></i> {{$t('common.back')}} {{$t('common.to')}} {{$t('document_templates.title')}}
                    </router-link>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-12 col-lg-7">
                <div class="panel">
                    <div class="panel__header">
                        <h2>
                            <i class="panel__header__icon icons icon-doc"></i>
                            <span>{{$t('document_template.title_editor')}}</span>
                        </h2>
                    </div>
                    <div class="panel__body">
                        <form @submit.prevent="save" class="form">
                            <div class="row">
                                <div class="col-md-8 col-lg-6">
                                    <div class="form__item">
                                        <i class="form__icon icons icon-notebook"></i>
                                        <input type="text"
                                               required
                                               v-model="template.name"
                                               :class="['form__input--text', {'has-value': template.name}]"/>
                                        <label class="form__label">
                                            {{$t('forms.document_template.template_name')}}
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <editor-menu-bar :editor="editor">
                                <div slot-scope="{commands, isActive}">
                                    <div class="tiptap__buttons">
                                        <button @click.prevent="commands.bold"
                                                :class="['tiptap__button', {'toggle-enabled': isActive.bold()}]">
                                            <i class="mdi mdi-format-bold"></i>
                                        </button>
                                        <button @click.prevent="commands.italic"
                                                :class="['tiptap__button', {'toggle-enabled': isActive.italic()}]">
                                            <i class="mdi mdi-format-italic"></i>
                                        </button>
                                        <button @click.prevent="commands.underline"
                                                :class="['tiptap__button', {'toggle-enabled': isActive.underline()}]">
                                            <i class="mdi mdi-format-underline"></i>
                                        </button>
                                        <button @click.prevent="commands.code"
                                                :class="['tiptap__button', {'toggle-enabled': isActive.code()}]">
                                            <i class="mdi mdi-code-tags"></i>
                                        </button>
                                        <button @click.prevent="commands.paragraph"
                                                :class="['tiptap__button', {'toggle-enabled': isActive.paragraph()}]">
                                            <i class="mdi mdi-format-paragraph"></i>
                                        </button>
                                        <button v-for="level in 3"
                                                @click.prevent="commands.heading({ level })"
                                                :class="['tiptap__button', {'toggle-enabled': isActive.heading({ level })}]">
                                            <i :class="['mdi mdi-format-header-' + level]"></i>
                                        </button>
                                        <button @click.prevent="commands.horizontal_rule"
                                                class="tiptap__button">
                                            <i class="mdi mdi-minus"></i>
                                        </button>
                                        <button @click.prevent="commands.bullet_list"
                                                :class="['tiptap__button', {'toggle-enabled': isActive.bullet_list()}]">
                                            <i class="mdi mdi-format-list-bulleted"></i>
                                        </button>
                                        <button @click.prevent="commands.ordered_list"
                                                :class="['tiptap__button', {'toggle-enabled': isActive.ordered_list()}]">
                                            <i class="mdi mdi-format-list-numbered"></i>
                                        </button>
                                        <button
                                                @click.prevent="commands.createTable({rowsCount: 3, colsCount: 3, withHeaderRow: false})"
                                                :class="['tiptap__button', {'toggle-enabled': isActive.table()}]">
                                            <i class="mdi mdi-table"></i>
                                        </button>

                                        <button @click.prevent="commands.undo"
                                                class="tiptap__button">
                                            <i class="mdi mdi-undo"></i>
                                        </button>
                                        <button @click.prevent="commands.redo"
                                                class="tiptap__button">
                                            <i class="mdi mdi-redo"></i>
                                        </button>

                                        <div v-if="isActive.table()" class="tiptap__table_buttons">
                                            <button @click.prevent="commands.deleteTable"
                                                    class="tiptap__button">
                                                <i class="mdi mdi-table-remove"></i>
                                            </button>
                                            <button @click.prevent="commands.addColumnBefore"
                                                    class="tiptap__button">
                                                <i class="mdi mdi-table-column-plus-before"></i>
                                            </button>
                                            <button @click.prevent="commands.deleteColumn"
                                                    class="tiptap__button">
                                                <i class="mdi mdi-table-column-remove"></i>
                                            </button>
                                            <button @click.prevent="commands.addColumnAfter"
                                                    class="tiptap__button">
                                                <i class="mdi mdi-table-column-plus-after"></i>
                                            </button>
                                            <button @click.prevent="commands.addRowBefore"
                                                    class="tiptap__button">
                                                <i class="mdi mdi-table-row-plus-before"></i>
                                            </button>
                                            <button @click.prevent="commands.deleteRow"
                                                    class="tiptap__button">
                                                <i class="mdi mdi-table-row-remove"></i>
                                            </button>
                                            <button @click.prevent="commands.addRowAfter"
                                                    class="tiptap__button">
                                                <i class="mdi mdi-table-row-plus-after"></i>
                                            </button>
                                            <button @click.prevent="commands.toggleCellMerge"
                                                    class="tiptap__button">
                                                <i class="mdi mdi-table-merge-cells"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </editor-menu-bar>
                            <div class="tiptap">
                                <editor-content :editor="editor"/>
                            </div>
                            <div class="row">
                                <div class="col-12">
                                    <div class="form__item--left_aligned">
                                        <div class="checkbox">
                                            <input type="checkbox" v-model="template.requires_approval"
                                                   id="checkbox:requires_approval" class="checkbox__cb"/>
                                            <label for="checkbox:requires_approval" class="checkbox__label">
                                                {{$t('forms.document_template.requires_approval')}}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="form__buttons">
                                <div class="row">
                                    <div class="col-6"></div>
                                    <div class="col-6">
                                        <button type="submit"
                                                class="form__button button--cta">
                                            {{$t('common.save')}}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div class="suggestion_list" v-show="popupVisible" ref="suggestions">
                                <template v-if="popupHasResults">
                                    <div v-for="(tag, index) in popup.items" :key="tag.id"
                                         @click="insertPopupSelection(tag)"
                                         :class="{ 'is-selected': popup.userSelectIndex === index }"
                                         class="suggestion_list__item">
                                        {{tag.name}}
                                    </div>
                                </template>
                                <div v-else class="suggestion_list__item is-empty">
                                    {{$t('document_template.suggestions_empty')}}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
                <standard-tags @insert="insertTag"/>

            </div>
            <div class="col-12 col-lg-5">

                <string-tags :tags="template.fields" @validate="verifyTagName" @insert="insertTag"/>


                <choice-tags :tags="template.fields" @validate="verifyTagName" @insert="insertTag"/>
            </div>
        </div>
    </div>
</template>
<script>
    import Vue from 'vue';
    import {Component, Watch} from 'vue-property-decorator';
    import {Editor, EditorContent, EditorMenuBar} from 'tiptap';
    import {
        Bold, Italic, Underline, Code,
        Heading,
        HorizontalRule,
        OrderedList,
        BulletList,
        ListItem,
        History,
        Table, TableHeader, TableRow, TableCell,
    } from 'tiptap-extensions';
    import StandardTags from '@/components/panels/document-templates/StandardTags';
    import StringTags from '@/components/panels/document-templates/StringTagForm';
    import ChoiceTags from '@/components/panels/document-templates/ChoiceTagForm';
    import tippy from 'tippy.js/esm';
    import 'tippy.js/index.css';
    import TagPlugin from '@/components/tiptap/TagPlugin';
    import {
        GET_DOCUMENT_TEMPLATE,
        NEW_DOCUMENT_TEMPLATE,
        SAVE_DOCUMENT_TEMPLATE
    } from '@/store/modules/document-template';
    import DocumentTemplate from '@/models/DocumentTemplate';
    import {NEW_TEMPLATE_TEXT} from '@/models/DocumentTemplate';
    import Fuse from 'fuse.js';

    @Component({
        components: {
            StandardTags,
            StringTags,
            ChoiceTags,
            EditorContent,
            EditorMenuBar,
        },
        beforeRouteEnter(to, from, next) {
            next(vm => vm.installHotkey());
        },
        beforeRouteLeave(to, from, next) {
            this.uninstallHotkey();
            next();
        }
    })
    export default class DocumentTemplateEditor extends Vue {
        editor = null;
        saving = false;
        popup = {
            query: null,
            items: [],
            range: null,
            userSelectIndex: 0,
            command: null,
            elem: null, // tippy.js element storage
            maxSearchCount: 10,
        };
        surpressFetch = false;

        get template() {
            return this.$store.state.document_template.template;
        }

        get standardTags() {
            return this.$store.state.document_template_tags.tags;
        }

        get fuse() {
            console.log('[document-template]', '[fuse]', 'creating instance');
            // This computed property automatically updates the fuse instance when its dependencies change (the allTags array)
            const settings = {
                theshold: 0.2,
                keys: [
                    {name: 'name', weight: 0.8},
                    {name: 'description', weight: 0.2},
                ],
            };
            return new Fuse(this.allTags, settings);
        }

        created() {
            this.initEditor();
            this.loadTemplate(this.templateId);
        }

        get templateId() {
            return this.$route.params.id;
        }

        @Watch('templateId')
        loadTemplate(id) {
            console.log('[document-template]', 'template-id changed', id);
            if (this.surpressFetch) {
                this.surpressFetch = false;
                console.log('[document-template]', 'surpressed fetch');
                return;
            }
            this.destroyPopup();
            if (id === 'new') {
                const template = new DocumentTemplate();
                template.contents = NEW_TEMPLATE_TEXT;
                this.$store.dispatch(NEW_DOCUMENT_TEMPLATE, {template});
            } else {
                if (!this.saving) {
                    this.$store.dispatch(GET_DOCUMENT_TEMPLATE, {id}).then(template => {
                        console.log('[document-template]', 'loaded template', template);
                    });
                } else {
                    console.log('[document-template]', 'Skipping reload due to saving state');
                }
            }
        }

        initEditor() {
            const mention = new TagPlugin({
                items: () => {
                    return this.allTags;
                },
                onEnter: ({query, items, range, command, virtualNode}) => {
                    console.log('[mention]', 'onEnter');
                    this.popup.query = query;
                    this.popup.items = items;
                    this.popup.range = range;
                    this.popup.userSelectIndex = 0;
                    this.popup.command = command;
                    this.renderPopup(virtualNode);
                },
                onChange: ({query, items, range, virtualNode}) => {
                    console.log('[mention]', 'onChange');
                    this.popup.query = query;
                    this.popup.items = items;
                    this.popup.range = range;
                    this.popup.userSelectIndex = 0;
                    this.renderPopup(virtualNode);
                },
                onExit: () => {
                    console.log('[mention]', 'onExit');
                    this.popup.query = null;
                    this.popup.items = [];
                    this.popup.range = null;
                    this.popup.command = null;
                    this.popup.userSelectIndex = 0;
                    this.destroyPopup();
                },
                onKeyDown: ({event}) => {
                    return this.handlePopupKey(event);
                },
                onFilter: (items, query) => {
                    const results = query ? this.fuse.search(query) : items;
                    return results.slice(0, this.popup.maxSearchCount);
                },
            });
            this.editor = new Editor({
                content: '<p>Geachte <span>[client-naam]</span></p>' +
                '<p>Hierbij willen we je laten weten dat je verzoek gewijzigd is door <span>[adviseur-naam]</span></p>' +
                '<h1>Rapport</h1>' +
                '<p>Ons rapport bevant relevante informatie die je wilt zien!</p>',
                extensions: [
                    new Bold(), new Italic(), new Underline(),
                    new Code(),
                    new Heading({levels: [1, 2, 3]}),
                    new History(),
                    mention,
                    new Table(),
                    new TableHeader(),
                    new TableRow(),
                    new TableCell(),
                    new HorizontalRule(),
                    new OrderedList(),
                    new BulletList(),
                    new ListItem(),
                ],
            });
        }

        beforeDestroy() {
            console.log('[document-template]', 'beforeDestroy');
            this.destroyPopup();
            this.editor.destroy();
        }

        @Watch('template.contents')
        updateEditorContent(value) {
            console.log('[document-template]', 'vuex template content updated', value);
            if (value) {
                this.editor.setContent(value);
            } else {
                this.editor.clearContent();
            }
        }

        verifyTagName(event) {
            const name = event.name;
            event.accepted = (
                name &&
                name.length &&
                !this.allTags.some(field => field.name === name)
            );
        }

        get allTags() {
            return this.standardTags.concat(this.template.fields);
        }

        renderPopup(virtualNode) {
            console.log('[popup]', 'render');
            if (this.popup.elem) return;
            this.popup.elem = tippy(virtualNode, {
                content: this.$refs.suggestions,
                trigger: 'mouseenter',
                interactive: true,
                theme: 'dark',
                placement: 'top-start',
                ignoreAttributes: true,
                inertia: true,
                duration: [400, 200],
                showOnInit: true,
                arrow: true,
                arrowType: 'round',
                flipOnUpdate: false,
            });
        }

        updated() {
            // As of tippy.js@4.0.0 this is required to update the instance when using an element as tooltip content
            if (this.popup.elem) {
                this.popup.elem.popperInstance.scheduleUpdate();
            }
        }

        destroyPopup() {
            console.log('[popup]', 'destroy');
            if (this.popup.elem) {
                this.popup.elem.destroy();
                this.popup.elem = null;
            }
        }

        handlePopupKey(event) {
            console.log('[popup]', 'keycode', event.keyCode);
            const popup = this.popup;
            const itemCount = popup.items.length;
            switch (event.keyCode) {
            case 38:
                // up arrow
                popup.userSelectIndex = (popup.userSelectIndex + (itemCount - 1)) % itemCount;
                return true;
            case 40:
                //down arrow
                popup.userSelectIndex = (popup.userSelectIndex + 1) % itemCount;
                return true;
            case 13: //enter
            case 221: // ]
                // TODO: Implement more keys to match navigations
                // TODO: Implement space to complete when navigation has been focussed
                // TODO: Let escape cancel focus on auto complete bubble
                this.insertPopupSelection();
                return true;
            default:
                return false;
            }
        }

        insertPopupSelection(selected) {
            if (!selected) {
                console.log('[popup] insert by index', this.popup.userSelectIndex, this.popup.items.map(item => item.name));
                selected = this.popup.items[this.popup.userSelectIndex];
            } else {
                console.log('[popup] insert by click');
            }
            this.insertTag(selected);
        }

        insertTag(tag) {
            console.log('[insert-tag]', this.popup.command ? 'popup-command' : 'editor-command', tag);
            const attrs = {
                tag: tag.name,
            };
            if (this.popup.command) {
                this.popup.command({
                    range: this.popup.range,
                    attrs,
                });
                this.editor.focus();
            } else {
                this.editor.commands.tag(attrs);
                // TODO: Insert space after insertion
                // https://github.com/scrumpy/tiptap/blob/master/packages/tiptap-extensions/src/plugins/Suggestions.js#L140
            }
        }

        get popupVisible() {
            return this.popup.query || this.popupHasResults;
        }

        get popupHasResults() {
            return this.popup.items.length > 0;
        }

        save() {
            this.template.contents = this.editor.getHTML();
            this.$store.dispatch(SAVE_DOCUMENT_TEMPLATE, {})
                .then((template) => {
                    if (this.templateId === 'new') {
                        this.surpressFetch = true;
                        this.$router.replace({
                            params: {
                                id: template.id
                            }
                        });
                        this.$notify.success(this.$t('notify.document_template.create_success'));
                    } else {
                        this.$notify.success(this.$t('notify.document_template.save_success'));
                    }
                })
                .catch(err => {
                    this.$notify.error(this.$t('notify.document_template.save_error'));
                })
                .finally(() => {
                    this.saving = false;
                });
        }

        get errors() {
            return this.$store.state.document_template.errors;
        }

        get loading() {
            return !this.saving && this.$store.state.document_template.loading;
        }

        installHotkey() {
            console.log('[document-template]', '[hotkey]', 'install');
            console.assert(!this._hotkeyHandler, 'hotkey handler already installed');
            this._hotkeyHandler = this.hotkeyHandler.bind(this);
            document.addEventListener('keydown', this._hotkeyHandler, false);
        }

        uninstallHotkey() {
            console.log('[document-template]', '[hotkey]', 'uninstall');
            if (this._hotkeyHandler) {
                document.removeEventListener('keydown', this._hotkeyHandler, false);
                this._hotkeyHandler = null;
            }
        }

        hotkeyHandler(event) {
            const platformCtrl = (window.navigator.platform.match('Mac') ? event.metaKey : event.ctrlKey);
            if (platformCtrl && event.keyCode === 83) {
                // Ctrl+S / Cmd+S
                // Prevent saving the page
                console.log('[document-template]', '[hotkey]', 'saving');
                event.preventDefault();
                // Save document
                this.save();
            }
        }
    }
</script>



// WEBPACK FOOTER //
// src/pages/DocumentTemplate.vue